import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Eye, EyeOff, ArrowLeft, Loader2 } from "lucide-react";

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = isLogin
      ? await login(email, password)
      : await register(name, email, password);
    setLoading(false);
    if (result.success) {
      navigate(result.role === "admin" ? "/admin" : "/marketplace");
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 circuit-bg" data-testid="auth-page">
      {/* Gradient orbs */}
      <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-ib-blue/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/3 right-1/4 w-64 h-64 bg-ib-lime/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="relative z-10 w-full max-w-md">
        {/* Back to home */}
        <Link
          to="/"
          data-testid="auth-back-home"
          className="inline-flex items-center gap-2 text-ib-text-secondary hover:text-ib-blue font-outfit text-sm mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Logo */}
        <div className="mb-8">
          <a href="/" className="flex items-baseline gap-0">
            <span className="font-unbounded font-black text-2xl tracking-tighter text-ib-text">Iron</span>
            <span className="font-jetbrains font-bold text-2xl text-ib-blue">Byte</span>
          </a>
          <p className="font-manrope text-sm text-ib-text-secondary mt-2">
            {isLogin ? "Sign in to your account" : "Create your ironByte account"}
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="glass rounded-xl p-8 space-y-5"
          data-testid="auth-form"
        >
          {error && (
            <div data-testid="auth-error" className="p-3 rounded-lg bg-ib-negative/10 border border-ib-negative/20 text-ib-negative font-manrope text-sm">
              {error}
            </div>
          )}

          {!isLogin && (
            <div>
              <label className="font-outfit text-xs text-ib-text-secondary mb-1.5 block">Full Name</label>
              <Input
                data-testid="auth-name-input"
                placeholder="John Smith"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required={!isLogin}
                className="bg-ib-surface border-ib-steel/30 text-ib-text placeholder:text-ib-text-muted font-manrope text-sm focus:border-ib-blue"
              />
            </div>
          )}

          <div>
            <label className="font-outfit text-xs text-ib-text-secondary mb-1.5 block">Email</label>
            <Input
              data-testid="auth-email-input"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-ib-surface border-ib-steel/30 text-ib-text placeholder:text-ib-text-muted font-manrope text-sm focus:border-ib-blue"
            />
          </div>

          <div>
            <label className="font-outfit text-xs text-ib-text-secondary mb-1.5 block">Password</label>
            <div className="relative">
              <Input
                data-testid="auth-password-input"
                type={showPwd ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-ib-surface border-ib-steel/30 text-ib-text placeholder:text-ib-text-muted font-manrope text-sm focus:border-ib-blue pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ib-text-muted hover:text-ib-text"
                data-testid="auth-toggle-password"
              >
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="auth-submit-btn"
            className="w-full bg-ib-blue hover:bg-ib-blue/90 text-white font-outfit gap-2 py-6 rounded-full transition-all hover:shadow-[0_0_30px_rgba(0,123,255,0.3)]"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {isLogin ? "Sign In" : "Create Account"}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => { setIsLogin(!isLogin); setError(""); }}
              data-testid="auth-toggle-mode"
              className="font-manrope text-sm text-ib-text-secondary hover:text-ib-blue transition-colors"
            >
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
